import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {
  DataInProgress,
  GetHeaderMain,
  ServerResponse,
  GetInterestsModel,
  SignupResponseModel,
  SignUpWithScheduleVisit,
  SocialLoginEnum,
  TypeDataInProgress,
  ServerObjResponse,
  RequestSchedules,
} from '@models';
import { dateZoneMex, DeleteObjectProperties } from '@utilities';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ScheduleVisitService {
  private scheduleVisit = environment.SCHEDULE_VISIT;

  public dataInProgress = new BehaviorSubject<Partial<DataInProgress>>(
    undefined
  );
  private readonly itemNameLocalStorage = 'ggiScheduleVisit';

  constructor(private readonly http: HttpClient) {}

  /**
   * Get header information
   * @param params Of type GetHeaderMain
   */
  public getHeader(data: GetHeaderMain): Observable<ServerResponse> {
    return this.http
      .get<ServerResponse>(this.scheduleVisit.HEADER, {
        params: { ...data },
      })
      .pipe(
        tap(({ response: { projectLogo } }) =>
          this.saveDataInProgress(
            { projectLogo },
            TypeDataInProgress.projectLogo
          )
        )
      );
  }

  public getInterests(typeProject: string): Observable<GetInterestsModel> {
    const path = this.scheduleVisit.INTERESTS.replace(
      '{typeProject}',
      typeProject
    );
    return this.http.get<GetInterestsModel>(path);
  }

  public signup(
    data: SignUpWithScheduleVisit
  ): Observable<SignupResponseModel> {
    return this.http.post<SignupResponseModel>(this.scheduleVisit.SIGNUP, data);
  }

  public scheduleAnonymousVisit(
    data: SignUpWithScheduleVisit
  ): Observable<SignupResponseModel> {
    return this.http.post<SignupResponseModel>(
      this.scheduleVisit.ANONYMUS,
      data
    );
  }

  public getCalendarAvailable(
    data: RequestSchedules
  ): Observable<ServerResponse> {
    return this.http.get<ServerResponse>(
      `${this.scheduleVisit.AVAILABLE_BROKERS}`,
      {
        params: { ...data },
      }
    );
  }

  public checkIn(
    data: SignUpWithScheduleVisit
  ): Observable<SignupResponseModel> {
    return this.http.post<SignupResponseModel>(this.scheduleVisit.VISIT, data);
  }

  public signInWithSocialMedia(
    data: SignUpWithScheduleVisit
  ): Observable<SignupResponseModel> {
    if (data.originAccount === SocialLoginEnum.GOOGLE) {
      return this.http.post<SignupResponseModel>(
        this.scheduleVisit.GOOGLE,
        data
      );
    } else {
      return this.http.post<SignupResponseModel>(
        this.scheduleVisit.FACEBOOK,
        data
      );
    }
  }

  /**
   * Save steps for schedule visit in app memory
   * @param data Object with data selected for user
   */
  public saveDataInProgress(
    { ...data }: Partial<DataInProgress>,
    type: TypeDataInProgress
  ): void {
    let progress: Partial<DataInProgress> = JSON.parse(
      localStorage.getItem(this.itemNameLocalStorage)
    );
    if (progress) {
      switch (type) {
        case TypeDataInProgress.project:
          const { project, typeProject } = data;
          progress = { ...progress, project, typeProject };
          break;
        case TypeDataInProgress.selectProperty:
          const { propertyId, propertyName } = data;

          progress = { ...progress, propertyId, propertyName };
          break;

        case TypeDataInProgress.projectLogo:
          const { projectLogo } = data;

          progress = { ...progress, projectLogo };
          break;

        case TypeDataInProgress.day:
          const { day } = data;

          progress = { ...progress, day };
          break;

        case TypeDataInProgress.broker:
          const { broker } = data;

          progress = { ...progress, broker };
          break;

        case TypeDataInProgress.hour:
          const { hour } = data;

          progress = { ...progress, hour };
          break;

        case TypeDataInProgress.preferences:
          const { interests, interestExtraNotes, answer, question } = data;
          progress = {
            ...progress,
            interests,
            interestExtraNotes,
            answer,
            question,
          };
          break;

        case TypeDataInProgress.newAccount:
          const {
            firstName,
            lastName,
            password,
            email,
            acceptPoliciesAndTerms,
            phone,
            accessToken,
            uid,
            profilePhoto,
            originAccount,
          } = data;

          progress = {
            ...progress,
            firstName,
            lastName,
            password,
            email,
            acceptPoliciesAndTerms,
            phone,
            accessToken,
            uid,
            profilePhoto,
            originAccount,
          };
          break;
      }
    } else {
      progress = data;
    }

    localStorage.setItem(this.itemNameLocalStorage, JSON.stringify(progress));
    this.dataInProgress.next(progress);
  }

  public clearDataInProgress(): void {
    localStorage.removeItem(this.itemNameLocalStorage);
    this.dataInProgress.next(undefined);
  }

  public getDataInProgress(): DataInProgress {
    return JSON.parse(localStorage.getItem(this.itemNameLocalStorage));
  }

  public getSignUpWithScheduleVisitData(): SignUpWithScheduleVisit {
    const {
      project,
      typeProject,
      propertyId,
      projectLogo,

      day,
      hour,

      interests,
      interestExtraNotes,
      answer,
      question,

      firstName,
      lastName,
      password,
      phone,
      email,
      acceptPoliciesAndTerms,
      accessToken,
      uid,
      profilePhoto,
      originAccount,
      broker,
    } = this.getDataInProgress();

    const visit = {
      date: dateZoneMex(`${day} ${hour}`),
      broker,
    };

    const data: SignUpWithScheduleVisit = {
      email,
      acceptPoliciesAndTerms,
      password,
      name: { firstName, lastName },
      phone,
      interests,
      interestExtraNotes,
      answer,
      question,
      project,
      projectLogo,
      typeProject,
      typeProperty: propertyId,
      visit,
      accessToken,
      uid,
      profilePhoto,
      originAccount,
    };

    DeleteObjectProperties(data);

    return data;
  }

  public getQuestion(): Observable<ServerObjResponse> {
    return this.http.get<ServerObjResponse>(environment.CLIENT_QUESTION);
  }
}
